import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { translationsClient } from '../lib/translations';
import { detectLanguage } from '../services/languageDetection';
import { logger } from '../lib/logger';
import { sessionTracker } from '../lib/analytics/sessionTracker';

// Types pour les langues supportées
export type SupportedLanguages = 'fr' | 'en' | 'es' | 'it' | 'pt' | 'de' | 'nl' | 'ja' | 'zh' | 'ru';

// Configuration des langues
export const languages: { [key in SupportedLanguages]: string } = {
  fr: 'Français',
  en: 'English',
  es: 'Español',
  it: 'Italiano',
  pt: 'Português',
  de: 'Deutsch',
  nl: 'Nederlands',
  ja: '日本語',
  zh: '中文',
  ru: 'Русский'
};

// Fonction pour charger les traductions depuis Supabase
const loadTranslations = async () => {
  try {
    const [generalTranslations, categoryTranslations] = await Promise.all([
      translationsClient.getGeneralTranslations(),
      translationsClient.getCategoryTypeTranslations()
    ]);

    return {
      ...generalTranslations,
      ...categoryTranslations
    };

  } catch (error) {
    logger.error('Error loading translations', {
      category: 'Translation',
      data: { error }
    });
    return {};
  }
};

// Initialisation de i18next
const initI18n = async () => {
  const translations = await loadTranslations();
  const detectedLanguage = detectLanguage();

  // Initialiser i18next avec React
  await i18n
    .use(initReactI18next)
    .use(LanguageDetector)
    .init({
      resources: translations,
      lng: detectedLanguage,
      fallbackLng: 'fr',
      supportedLngs: Object.keys(languages),
      detection: {
        order: ['localStorage', 'navigator'],
        lookupLocalStorage: 'i18nextLng',
        caches: ['localStorage'],
        cookieMinutes: 10080, // 7 jours
        checkWhitelist: true
      },
      interpolation: {
        escapeValue: false
      },
      load: 'languageOnly',
      cleanCode: true,
      lowerCaseLng: true,
      // Désactiver le rechargement automatique des ressources
      partialBundledLanguages: true,
      // Optimiser la gestion de la mémoire
      updateMissing: false,
      saveMissing: false,
      // Optimiser les performances
      keySeparator: false,
      nsSeparator: false,
      // Désactiver les fonctionnalités non utilisées
      appendNamespaceToMissingKey: false,
      parseMissingKeyHandler: false,
      // Cache en mémoire
      cache: {
        enabled: true,
        expirationTime: 24 * 60 * 60 * 1000 // 24 heures
      }
    });

  // Update session tracker with the detected language
  sessionTracker.updateLanguage(detectedLanguage);

  return i18n;
};

export default initI18n;